/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListMetaMcpMembersRequest,
  ListMetaMcpMembersSecurity,
} from "../models/operations/listmetamcpmembers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildMetaMcpMembersQuery,
  MetaMcpMembersQueryData,
  prefetchMetaMcpMembers,
  queryKeyMetaMcpMembers,
} from "./metaMcpMembers.core.js";
export {
  buildMetaMcpMembersQuery,
  type MetaMcpMembersQueryData,
  prefetchMetaMcpMembers,
  queryKeyMetaMcpMembers,
};

export type MetaMcpMembersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMetaMcpMembers metaMcp
 *
 * @remarks
 * List the members of a meta MCP server, ordered by sort order
 */
export function useMetaMcpMembers(
  request: ListMetaMcpMembersRequest,
  security?: ListMetaMcpMembersSecurity | undefined,
  options?: QueryHookOptions<MetaMcpMembersQueryData, MetaMcpMembersQueryError>,
): UseQueryResult<MetaMcpMembersQueryData, MetaMcpMembersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildMetaMcpMembersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMetaMcpMembers metaMcp
 *
 * @remarks
 * List the members of a meta MCP server, ordered by sort order
 */
export function useMetaMcpMembersSuspense(
  request: ListMetaMcpMembersRequest,
  security?: ListMetaMcpMembersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    MetaMcpMembersQueryData,
    MetaMcpMembersQueryError
  >,
): UseSuspenseQueryResult<MetaMcpMembersQueryData, MetaMcpMembersQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildMetaMcpMembersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setMetaMcpMembersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      metaMcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: MetaMcpMembersQueryData,
): MetaMcpMembersQueryData | undefined {
  const key = queryKeyMetaMcpMembers(...queryKeyBase);

  return client.setQueryData<MetaMcpMembersQueryData>(key, data);
}

export function invalidateMetaMcpMembers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      metaMcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "metaMcp", "listMembers", ...queryKeyBase],
  });
}

export function invalidateAllMetaMcpMembers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "metaMcp", "listMembers"],
  });
}
